import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Save, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

import { LEAD_STAGES, getBackendValueByStageId, getStageIdByBackendValue } from './constants';

export const LeadDetailModal = ({ isOpen, onClose, lead, onUpdate, onDelete }) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Estado local para edición
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    value: '',
    product: '',
    notes: '',
    priority: 'Media',
    stage: 'new' // ID
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        phone: lead.phone || '',
        email: lead.email || '',
        company: lead.company || '',
        value: lead.value || '',
        product: lead.product || '',
        notes: lead.notes || '',
        priority: lead.priority || 'Media',
        stage: lead.stage || 'new' // lead.stage en board ya es ID
      });
      setIsEditing(true); // CAMBIO: Abrir directamente en modo edición
    }
  }, [lead, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return toast.error("El nombre es obligatorio");

    setLoading(true);
    try {
      // Mapear campos visuales a estructura de backend (LeadUpdate)
      const payload = {
        name: formData.name,
        phone: formData.phone || null,
        email: formData.email || null,
        estimated_annual_premium: parseFloat(formData.value) || 0,
        policy_type: formData.product, // CRÍTICO: Enviar el producto al backend
        notes: formData.notes, 
        priority: formData.priority,
        stage: getBackendValueByStageId(formData.stage) // Enviar valor string backend
      };

      await api.put(`/leads/${lead.id}`, payload);
      
      toast.success("Lead actualizado exitosamente");
      onUpdate(); // Recargar tablero
      setIsEditing(false);
      onClose();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Error al actualizar el lead");
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async () => {
    if (!window.confirm("¿Seguro que deseas eliminar este lead? Esta acción no se puede deshacer.")) return;
    
    setLoading(true);
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead eliminado");
      onDelete(lead.id); // Notificar al padre para quitar del estado local
      onClose();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Error al eliminar");
    } finally {
      setLoading(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex justify-between items-center pr-8">
            <DialogTitle className="text-xl">Editar Prospecto</DialogTitle>
            {isEditing && (
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="text-slate-600">
                Solo Ver
              </Button>
            )}
          </div>
          <DialogDescription>
            Modifica la información del prospecto y guarda los cambios.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                disabled={!isEditing} 
                className={!isEditing ? "bg-slate-50 border-transparent" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>Empresa / Organización</Label>
              <Input 
                name="company" 
                value={formData.company} 
                onChange={handleChange} 
                disabled={!isEditing}
                placeholder="Opcional"
                className={!isEditing ? "bg-slate-50 border-transparent" : ""}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                disabled={!isEditing}
                className={!isEditing ? "bg-slate-50 border-transparent" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                disabled={!isEditing}
                className={!isEditing ? "bg-slate-50 border-transparent" : ""}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valor Estimado ($)</Label>
              <Input 
                name="value" 
                type="number" 
                value={formData.value} 
                onChange={handleChange} 
                disabled={!isEditing}
                className={!isEditing ? "bg-slate-50 border-transparent" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label>Producto Interés</Label>
              {isEditing ? (
                <Select 
                  value={formData.product} 
                  onValueChange={(val) => handleSelectChange('product', val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Autos">Autos</SelectItem>
                    <SelectItem value="GMM">GMM</SelectItem>
                    <SelectItem value="Vida">Vida</SelectItem>
                    <SelectItem value="Hogar">Hogar</SelectItem>
                    <SelectItem value="Daños">Daños</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={formData.product} disabled className="bg-slate-50 border-transparent" />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea 
              name="notes" 
              value={formData.notes} 
              onChange={handleChange} 
              disabled={!isEditing} 
              rows={4}
              className={!isEditing ? "bg-slate-50 border-transparent resize-none" : ""}
            />
          </div>

          <div className="space-y-2">
            <Label>Etapa (Pipeline)</Label>
            {isEditing ? (
              <Select 
                value={formData.stage} 
                onValueChange={(val) => handleSelectChange('stage', val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona etapa" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STAGES.map(stage => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input 
                value={LEAD_STAGES.find(s => s.id === formData.stage)?.label || 'Desconocida'} 
                disabled 
                className="bg-slate-50 border-transparent" 
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          {isEditing ? (
            <>
              <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={loading}>
                Cancelar Edición
              </Button>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={handleDelete} disabled={loading} size="icon">
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button onClick={handleSave} disabled={loading} className="bg-indigo-600">
                  <Save className="mr-2 h-4 w-4" />
                  Guardar Cambios
                </Button>
              </div>
            </>
          ) : (
            <Button variant="outline" onClick={onClose} className="ml-auto">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
